"use client";

import { Toast } from "@base-ui/react/toast";
import React from "react";
import { useKeyboardHints } from "../../hooks/use-keyboard-hints";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import type { Omittable } from "../../lib/props";
import { GridSpinner } from "./grid-spinner";
import { Icon, type IconName } from "./icon";

// Notification anatomy: a title row (icon · text · dismiss ✕ at the top-right
// corner, on the title's own line) and, only when the toast has an action, a
// footer row of controls under the copy. Every control in a toast sits on
// the 24px rung (`xs` / `icon-xs`): the row is a footer, so the buttons
// trail like a dialog's — a muted `ghost` "Dismiss", then the offer as an
// `outline` button. Outline, not primary: the toast is transient feedback
// on the popover surface, and a filled call to action there shouts louder
// than the copy it follows; the hairline is what tells the offer apart from
// the sentence above it. The text column owns the width, so copy never
// wraps around a control. The ✕ is a 24px button on a 20px text line: the
// negative block margin centers it on the title without adding a pixel to
// the row, and the negative end margin lets it sit in the corner. Every
// button is `shape="square"`: the controls act ON the toast's rectangle, so
// they take its corners and read as its chrome, never as free-standing
// pills floating over it.
const TOAST_CONTENT_CLASS =
  "pointer-events-auto flex flex-col gap-3 overflow-hidden px-3.5 py-3 text-sm";
const TOAST_BODY_CLASS = "flex items-start gap-2";
const TOAST_TEXT_CLASS = "flex min-w-0 grow flex-col gap-0.5";
const TOAST_CLOSE_CLASS = "-my-0.5 -mr-1.5 shrink-0";
// 4px between the two controls: on the 24px rung an 8px gap reads as two
// separate offers, and a ghost beside an outline already has the hairline to
// tell them apart.
const TOAST_ACTIONS_CLASS = "flex items-center justify-end gap-1";
// The chord's keycaps INSIDE the action button, after its label, in the
// menu-shortcut grammar (plain muted type, wide tracking) rather than as
// bordered Kbd chips: a chip inside a 24px bordered control doubles the
// hairline. The pair reads as one phrase, "Undo ⌘Z".
const TOAST_ACTION_KEYS_CLASS =
  "font-medium text-muted-foreground/72 tracking-widest";

/**
 * A keyboard chord bound to a toast's action for as long as the toast is on
 * screen. `keys` are the keycaps rendered after the action's label; `matches`
 * decides whether a keydown IS the chord. The host writes the gate — a text
 * field's own undo, an open modal — because only it knows what else the
 * chord means where the toast was raised; the toast only knows the key was
 * pressed. When several toasts carry a matching chord the frontmost wins.
 */
export type ToastActionShortcut = Omittable<{
  keys: readonly string[];
  matches: (event: KeyboardEvent) => boolean;
}>;

const TOAST_ICONS: Record<string, IconName> = {
  error: "alert-circle",
  info: "info",
  success: "check-circle",
  warning: "alert-triangle",
};

/**
 * The leading glyph for a typed toast. Status types take their tinted
 * registry icon; `loading` takes the GridSpinner on the `xs` (14px) rung, a
 * step under the 16px status icons — nine lit cells at 16px read heavier
 * than a stroked glyph does. It centers in the same 16px column the icons
 * occupy, so the text starts in the same place whichever type a toast is
 * updated to. The wrapper is one text line tall (`h-lh`) and centers the
 * glyph on the title's first line.
 */
function ToastTypeIcon({
  type,
  icon,
}: Omittable<{
  type: string | undefined;
  icon?: IconName;
}>): React.ReactElement | null {
  if (type === "loading") {
    return (
      <div
        className="flex h-lh w-4 shrink-0 items-center justify-center"
        data-slot="toast-icon"
      >
        <GridSpinner color="subtle" size="xs" />
      </div>
    );
  }
  // A status type owns the glyph column; the caller-supplied `icon` fills it
  // only on a typeless toast, muted so the title keeps the emphasis.
  const name = type ? TOAST_ICONS[type] : icon;
  if (!name) return null;
  return (
    <div
      className="[&>svg]:h-lh [&>svg]:w-4 [&_svg]:pointer-events-none [&_svg]:shrink-0"
      data-slot="toast-icon"
    >
      <Icon
        className="text-muted-foreground in-data-[type=error]:text-destructive in-data-[type=info]:text-info in-data-[type=success]:text-success in-data-[type=warning]:text-warning"
        name={name}
      />
    </div>
  );
}

type SwipeDirection = "up" | "down" | "left" | "right";

type ToastData = Omittable<{
  rootProps?: Omit<
    React.ComponentProps<typeof Toast.Root>,
    "children" | "className" | "swipeDirection" | "toast"
  >;
  tooltipStyle?: boolean;
  /** Leading glyph for a typeless toast (an announcement rather than a
   *  status). Muted, in the same 16px column the status icons take; a status
   *  `type` owns that column and wins over this. */
  icon?: IconName;
  /** The offer button's hue. The action stays on the outline rung — a filled
   *  call to action shouts louder than the copy it follows — so the only
   *  alternative is `ai`, the same chip wearing the AI affordance ring, for
   *  an offer that leads somewhere special. */
  actionColor?: "neutral" | "ai";
  /** Binds a chord to `actionProps` while the toast stands. Positioned
   *  toasts only — an anchored toast is a hint, not a standing offer. */
  actionShortcut?: ToastActionShortcut;
}>;

/** The `actionShortcut` of a toast that can still take it: one that carries
 *  an action and is not already leaving. */
function activeActionShortcut(
  toast: Toast.Root.ToastObject,
): ToastActionShortcut | null {
  if (!toast.actionProps || toast.transitionStatus === "ending") return null;
  return (toast.data as ToastData | undefined)?.actionShortcut ?? null;
}

/**
 * One window listener for every toast on screen that bound a chord to its
 * action. A match CLICKS the toast's action button — the same path a pointer
 * takes, so the action's own handler and whatever it does to the toast run
 * unchanged — and the toasts are read newest-first, so the frontmost offer
 * is the one a chord takes.
 */
function useToastActionShortcuts(
  toasts: readonly Toast.Root.ToastObject[],
  actionButtons: React.RefObject<Map<string, HTMLButtonElement>>,
): void {
  const toastsRef = React.useRef(toasts);
  toastsRef.current = toasts;
  const bound = toasts.some((toast) => activeActionShortcut(toast) !== null);

  React.useEffect(() => {
    if (!bound) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      for (const toast of toastsRef.current) {
        const shortcut = activeActionShortcut(toast);
        if (!shortcut?.matches(event)) continue;
        const button = actionButtons.current.get(toast.id);
        // Backstop: a matching chord racing the button ref's teardown — the
        // toast is mid-unmount and its map entry is already gone.
        if (!button) return;
        event.preventDefault();
        button.click();
        return;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [bound, actionButtons]);
}

/** Single glyphs run together ("⌘Z"); a worded key keeps a space ("Ctrl Z"). */
function ToastActionKeys({
  keys,
}: Omittable<{ keys: readonly string[] }>): React.ReactElement | null {
  // The binding stays live; only its advertisement stands down where nothing
  // can press it.
  if (!useKeyboardHints()) return null;
  const glyphsOnly = keys.every((key) => [...key].length === 1);
  return (
    <kbd className={TOAST_ACTION_KEYS_CLASS} data-slot="toast-action-keys">
      {keys.join(glyphsOnly ? "" : " ")}
    </kbd>
  );
}

function getSwipeDirection(position: ToastPosition): SwipeDirection[] {
  const verticalDirection: SwipeDirection = position.startsWith("top")
    ? "up"
    : "down";

  if (position.includes("center")) {
    return [verticalDirection];
  }

  if (position.includes("left")) {
    return ["left", verticalDirection];
  }

  return ["right", verticalDirection];
}

function upsertReplayClassName(toast: Omittable<{
  type?: string;
  updateKey?: number;
}>): string | undefined {
  if (!toast.updateKey) return undefined;
  const isEven = toast.updateKey % 2 === 0;
  if (toast.type === "error") {
    return isEven ? "animate-toast-error-even" : "animate-toast-error-odd";
  }
  return isEven ? "animate-toast-success-even" : "animate-toast-success-odd";
}

function Toasts({
  position,
  region,
  portalProps,
}: Omittable<{
  position: ToastPosition;
  region: ToastRegion;
  portalProps?: React.ComponentProps<typeof Toast.Portal>;
}>): React.ReactElement {
  const { toasts } = Toast.useToastManager();
  const swipeDirection = getSwipeDirection(position);
  // The action buttons by toast id, for the chord path to click.
  const actionButtons = React.useRef(new Map<string, HTMLButtonElement>());
  useToastActionShortcuts(toasts, actionButtons);

  return (
    <Toast.Portal data-slot="toast-portal" {...portalProps}>
      <Toast.Viewport
        className={cn(
          // The TOP stacking rung. Every popup positioner, dialog and sheet
          // viewport sits at z-50, so a toast raised while a menu is open
          // paints over the menu and stays clickable — a notification never
          // hides behind the thing that caused it. The one other tenant of
          // this rung is the tooltip positioner: a tooltip raised from a word
          // inside a toast must paint over the stack, and it takes no input,
          // so it can never hide a toast the way a popup could.
          "fixed z-60 flex w-[calc(100%-var(--toast-inset)*2-var(--toast-region-left)-var(--toast-region-right))] max-w-90",
          // The toast's own gap from the region edge, one 16px step at every
          // viewport: the region is usually a card that already stands off
          // the screen edge, so a larger inset stacks two gutters.
          "[--toast-inset:--spacing(4)]",
          // The region the anchors measure from, as viewport insets. Zero on
          // every edge = the viewport itself; `app-shell-islands` reads the
          // islands region's insets an AppShell publishes on the root (0px
          // where no shell is mounted, so a shell-less route degrades to the
          // viewport).
          "[--toast-region-top:0px] [--toast-region-right:0px] [--toast-region-bottom:0px] [--toast-region-left:0px]",
          "data-[region=app-shell-islands]:[--toast-region-top:var(--app-shell-islands-inset-top,0px)]",
          "data-[region=app-shell-islands]:[--toast-region-right:var(--app-shell-islands-inset-right,0px)]",
          "data-[region=app-shell-islands]:[--toast-region-bottom:var(--app-shell-islands-inset-bottom,0px)]",
          "data-[region=app-shell-islands]:[--toast-region-left:var(--app-shell-islands-inset-left,0px)]",
          // Vertical positioning
          "data-[position*=top]:top-[calc(var(--toast-inset)+var(--toast-region-top))]",
          "data-[position*=bottom]:bottom-[calc(var(--toast-inset)+var(--toast-region-bottom))]",
          // Horizontal positioning
          "data-[position*=left]:left-[calc(var(--toast-inset)+var(--toast-region-left))]",
          "data-[position*=right]:right-[calc(var(--toast-inset)+var(--toast-region-right))]",
          // Centered on the REGION: the viewport midpoint, shifted by half the
          // difference between the two side insets.
          "data-[position*=center]:left-[calc(50%+(var(--toast-region-left)-var(--toast-region-right))/2)] data-[position*=center]:-translate-x-1/2",
        )}
        data-position={position}
        data-region={region}
        data-slot="toast-viewport"
      >
        {toasts.map((toast) => {
          const toastData = toast.data as ToastData | undefined;
          const actionShortcut = toastData?.actionShortcut;

          return (
            <Toast.Root
              key={toast.id}
              className={cn(
                "absolute z-[calc(9999-var(--toast-index))] h-(--toast-calc-height) w-full select-none rounded-lg border bg-[color-mix(in_srgb,var(--popover),var(--color-black)_calc(1%*max(0,var(--toast-index,0))))] not-dark:bg-clip-padding text-popover-foreground shadow-lg/5 [transition:transform_.5s_cubic-bezier(.22,1,.36,1),opacity_.5s,height_.15s,background-color_.5s] before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] data-expanded:bg-popover dark:bg-[color-mix(in_srgb,var(--popover),var(--color-black)_calc(6%*max(0,var(--toast-index,0))))] dark:data-expanded:bg-popover dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
                // Base positioning using data-position
                "data-[position*=right]:right-0 data-[position*=right]:left-auto",
                "data-[position*=left]:right-auto data-[position*=left]:left-0",
                "data-[position*=center]:right-0 data-[position*=center]:left-0",
                "data-[position*=top]:top-0 data-[position*=top]:bottom-auto data-[position*=top]:origin-[50%_calc(50%-50%*min(var(--toast-index,0),1))]",
                "data-[position*=bottom]:top-auto data-[position*=bottom]:bottom-0 data-[position*=bottom]:origin-[50%_calc(50%+50%*min(var(--toast-index,0),1))]",
                // Gap fill for hover
                "after:absolute after:left-0 after:h-[calc(var(--toast-gap)+1px)] after:w-full",
                "data-[position*=top]:after:top-full",
                "data-[position*=bottom]:after:bottom-full",
                // Define some variables
                "[--toast-calc-height:var(--toast-frontmost-height,var(--toast-height))] [--toast-gap:--spacing(3)] [--toast-peek:--spacing(3)] [--toast-scale:calc(max(0,1-(var(--toast-index)*.1)))] [--toast-shrink:calc(1-var(--toast-scale))]",
                // Define offset-y variable
                "data-[position*=top]:[--toast-calc-offset-y:calc(var(--toast-offset-y)+var(--toast-index)*var(--toast-gap)+var(--toast-swipe-movement-y))]",
                "data-[position*=bottom]:[--toast-calc-offset-y:calc(var(--toast-offset-y)*-1+var(--toast-index)*var(--toast-gap)*-1+var(--toast-swipe-movement-y))]",
                // Default state transform
                "data-[position*=top]:transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+(var(--toast-index)*var(--toast-peek))+(var(--toast-shrink)*var(--toast-calc-height))))_scale(var(--toast-scale))]",
                "data-[position*=bottom]:transform-[translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)-(var(--toast-index)*var(--toast-peek))-(var(--toast-shrink)*var(--toast-calc-height))))_scale(var(--toast-scale))]",
                // Limited state
                "data-limited:opacity-0",
                // Expanded state
                "data-expanded:h-(--toast-height)",
                "data-position:data-expanded:transform-[translateX(var(--toast-swipe-movement-x))_translateY(var(--toast-calc-offset-y))]",
                // Starting and ending animations
                "data-[position*=top]:data-starting-style:transform-[translateY(calc(-100%-var(--toast-inset)))]",
                "data-[position*=bottom]:data-starting-style:transform-[translateY(calc(100%+var(--toast-inset)))]",
                "data-ending-style:opacity-0",
                // Ending animations (direction-aware)
                "data-ending-style:not-data-limited:not-data-swipe-direction:transform-[translateY(calc(100%+var(--toast-inset)))]",
                "data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-100%-var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                "data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+100%+var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                "data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-100%-var(--toast-inset)))]",
                "data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+100%+var(--toast-inset)))]",
                // Ending animations (expanded)
                "data-expanded:data-ending-style:data-[swipe-direction=left]:transform-[translateX(calc(var(--toast-swipe-movement-x)-100%-var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                "data-expanded:data-ending-style:data-[swipe-direction=right]:transform-[translateX(calc(var(--toast-swipe-movement-x)+100%+var(--toast-inset)))_translateY(var(--toast-calc-offset-y))]",
                "data-expanded:data-ending-style:data-[swipe-direction=up]:transform-[translateY(calc(var(--toast-swipe-movement-y)-100%-var(--toast-inset)))]",
                "data-expanded:data-ending-style:data-[swipe-direction=down]:transform-[translateY(calc(var(--toast-swipe-movement-y)+100%+var(--toast-inset)))]",
                upsertReplayClassName(toast),
              )}
              {...toastData?.rootProps}
              // Portalled, so it inherits no surface scope — names its own,
              // and descendant `border-border` draws the popover-tuned
              // hairline.
              data-on="popover"
              data-position={position}
              swipeDirection={swipeDirection}
              toast={toast}
            >
              {/* The text block's flex-basis is its max-content width (the
                * unwrapped sentence), so the wrap decision reads the copy:
                * short copy keeps the action cluster inline; copy that would
                * not fit beside it sends the cluster to its own row under the
                * text, and the text then wraps at full width instead of in a
                * squeezed column. */}
              <Toast.Content
                className={cn(
                  TOAST_CONTENT_CLASS,
                  "transition-opacity duration-250 data-behind:not-data-expanded:pointer-events-none data-behind:opacity-0 data-expanded:opacity-100",
                )}
              >
                <div className={TOAST_BODY_CLASS}>
                  <ToastTypeIcon icon={toastData?.icon} type={toast.type} />

                  <div className={TOAST_TEXT_CLASS}>
                    <Toast.Title
                      className="font-medium"
                      data-slot="toast-title"
                    />
                    <Toast.Description
                      className="text-muted-foreground"
                      data-slot="toast-description"
                    />
                  </div>
                  {/* A persistent toast (timeout 0) never leaves on its own,
                    * so it must carry an explicit dismiss control — swipe is
                    * the only other exit and nothing advertises it. */}
                  {toast.timeout === 0 && (
                    <Toast.Close
                      aria-label="Dismiss notification"
                      className={TOAST_CLOSE_CLASS}
                      data-slot="toast-close"
                      render={
                        <Button shape="square" size="icon-xs" variant="ghost" />
                      }
                    >
                      <Icon name="x" size="sm" />
                    </Toast.Close>
                  )}
                </div>
                {toast.actionProps && (
                  <div className={TOAST_ACTIONS_CLASS}>
                    <Toast.Close
                      data-slot="toast-dismiss"
                      render={<Button shape="square" size="xs" variant="ghost" />}
                    >
                      Dismiss
                    </Toast.Close>
                    {/* Base UI forces `actionProps.children` onto the button
                      * whatever children are passed here, so the label and
                      * the keycaps compose through a render function: the
                      * chord sits inside the control, after the label, as
                      * one phrase. */}
                    <Toast.Action
                      data-slot="toast-action"
                      ref={(node) => {
                        if (node) actionButtons.current.set(toast.id, node);
                        else actionButtons.current.delete(toast.id);
                      }}
                      render={(props) => (
                        <Button
                          {...props}
                          shape="square"
                          size="xs"
                          color={toastData?.actionColor ?? "neutral"}
                          variant="outline"
                        >
                          <span data-slot="toast-action-label">
                            {props.children}
                          </span>
                          {actionShortcut && (
                            <ToastActionKeys keys={actionShortcut.keys} />
                          )}
                        </Button>
                      )}
                    />
                  </div>
                )}
              </Toast.Content>
            </Toast.Root>
          );
        })}
      </Toast.Viewport>
    </Toast.Portal>
  );
}

function AnchoredToasts({
  portalProps,
}: Omittable<{
  portalProps?: React.ComponentProps<typeof Toast.Portal>;
}>): React.ReactElement {
  const { toasts } = Toast.useToastManager();

  return (
    <Toast.Portal data-slot="toast-portal-anchored" {...portalProps}>
      <Toast.Viewport
        className="outline-none"
        data-slot="toast-viewport-anchored"
      >
        {toasts.map((toast) => {
          const toastData = toast.data as ToastData | undefined;
          const tooltipStyle = toastData?.tooltipStyle ?? false;
          const positionerProps = toast.positionerProps;

          if (!positionerProps?.anchor) {
            return null;
          }

          return (
            <Toast.Positioner
              key={toast.id}
              className="z-50 max-w-[min(--spacing(64),var(--available-width))]"
              data-slot="toast-positioner"
              sideOffset={positionerProps.sideOffset ?? 4}
              toast={toast}
            >
              <Toast.Root
                className={cn(
                  "relative text-balance border bg-popover not-dark:bg-clip-padding text-popover-foreground text-xs transition-[scale,opacity] before:pointer-events-none before:absolute before:inset-0 before:shadow-[0_1px_--theme(--color-black/4%)] data-ending-style:scale-98 data-starting-style:scale-98 data-ending-style:opacity-0 data-starting-style:opacity-0 dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
                  tooltipStyle
                    ? "rounded-md shadow-md/5 before:rounded-[calc(var(--radius-md)-1px)]"
                    : "rounded-lg shadow-lg/5 before:rounded-[calc(var(--radius-lg)-1px)]",
                  upsertReplayClassName(toast),
                )}
                {...toastData?.rootProps}
                // Portalled, so it inherits no surface scope — names its own,
                // and descendant `border-border` draws the popover-tuned
                // hairline.
                data-on="popover"
                data-slot="toast-popup"
                toast={toast}
              >
                {tooltipStyle ? (
                  <Toast.Content className="pointer-events-auto px-2 py-1">
                    <Toast.Title data-slot="toast-title" />
                  </Toast.Content>
                ) : (
                  <Toast.Content className={TOAST_CONTENT_CLASS}>
                    <div className={TOAST_BODY_CLASS}>
                      <ToastTypeIcon icon={toastData?.icon} type={toast.type} />

                      <div className={TOAST_TEXT_CLASS}>
                        <Toast.Title
                          className="font-medium"
                          data-slot="toast-title"
                        />
                        <Toast.Description
                          className="text-muted-foreground"
                          data-slot="toast-description"
                        />
                      </div>
                    </div>
                    {toast.actionProps && (
                      <div className={TOAST_ACTIONS_CLASS}>
                        <Toast.Close
                          data-slot="toast-dismiss"
                          render={<Button shape="square" size="xs" variant="ghost" />}
                        >
                          Dismiss
                        </Toast.Close>
                        <Toast.Action
                          data-slot="toast-action"
                          render={
                            <Button
                              shape="square"
                              size="xs"
                              color={toastData?.actionColor ?? "neutral"}
                          variant="outline"
                            />
                          }
                        />
                      </div>
                    )}
                  </Toast.Content>
                )}
              </Toast.Root>
            </Toast.Positioner>
          );
        })}
      </Toast.Viewport>
    </Toast.Portal>
  );
}

export const toastManager: ReturnType<typeof Toast.createToastManager> =
  Toast.createToastManager();

export const anchoredToastManager: ReturnType<typeof Toast.createToastManager> =
  Toast.createToastManager();

export type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

/**
 * What a position anchors to.
 *
 *  - `viewport`: the browser viewport's edges.
 *  - `app-shell-islands`: the islands of a mounted `AppShell` — the page
 *    card through the last docked right-panel island, none of the
 *    navigation. The shell publishes that region's viewport insets as
 *    `--app-shell-islands-inset-*` on the root and the viewport insets by
 *    them, so a left-anchored toast opens inside the page instead of over
 *    the navigation, while a right- or center-anchored one measures from the
 *    islands' outer edge and opens OVER an open island rather than being
 *    pushed off it. Without a shell the insets are 0px and the two regions
 *    coincide.
 */
export type ToastRegion = "viewport" | "app-shell-islands";

export type ToastProviderProps = Toast.Provider.Props &
  Omittable<{
  position?: ToastPosition;
  /** The region `position` anchors to. Defaults to the viewport. */
  region?: ToastRegion;
  portalProps?: React.ComponentProps<typeof Toast.Portal>;
}>;

export function ToastProvider({
  children,
  position = "bottom-right",
  region = "viewport",
  portalProps,
  ...props
}: ToastProviderProps): React.ReactElement {
  return (
    <Toast.Provider toastManager={toastManager} {...props}>
      {children}
      <Toasts portalProps={portalProps} position={position} region={region} />
    </Toast.Provider>
  );
}

export type AnchoredToastProviderProps = Toast.Provider.Props &
  Omittable<{
  portalProps?: React.ComponentProps<typeof Toast.Portal>;
}>;

export function AnchoredToastProvider({
  children,
  portalProps,
  ...props
}: AnchoredToastProviderProps): React.ReactElement {
  return (
    <Toast.Provider toastManager={anchoredToastManager} {...props}>
      {children}
      <AnchoredToasts portalProps={portalProps} />
    </Toast.Provider>
  );
}
